// src/slices/tenantManagement/reducer.ts
// Redux slice for Tenant Management

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TenantManagementState } from './types';
import { Tenant, TenantFilters } from 'pages/TenantManagement/types';
import {
  fetchTenants,
  fetchTenantDetail,
  createNewTenant,
  updateExistingTenant,
  deleteTenantById,
  checkCode,
} from './thunk';

const initialState: TenantManagementState = {
  tenants: [],
  selectedTenant: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
    filters: {
    searchTerm: '',
    status: 'All',
    type: undefined,
  },
  sortBy: 'createdAt',
  sortOrder: 'desc',
  loading: false,
  error: null,
  checkingCode: false,
  codeAvailable: false,
  modalState: {
    create: false,
    edit: false,
    delete: false,
  },
};

const tenantManagementSlice = createSlice({
  name: 'TenantManagement',
  initialState,
  reducers: {
    // Set selected tenant
    setSelectedTenant: (state, action: PayloadAction<Tenant | null>) => {
      state.selectedTenant = action.payload;
    },
    
    // Modal actions
    openModal: (state, action: PayloadAction<keyof typeof state.modalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof typeof state.modalState>) => {
      state.modalState[action.payload] = false;
    },
    
    // Filter actions
    setFilters: (state, action: PayloadAction<Partial<TenantFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },
    
    // Pagination actions
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    
    // Sort actions
    setSorting: (state, action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tenants
      .addCase(fetchTenants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.tenants = payload.items || payload.data || payload || [];
        state.pagination.totalItems = payload.totalItems ?? payload.totalCount ?? payload.total ?? state.tenants.length;
        state.pagination.totalPages = payload.totalPages ?? Math.ceil(state.pagination.totalItems / state.pagination.pageSize);
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch tenant detail
      .addCase(fetchTenantDetail.fulfilled, (state, action) => {
        state.selectedTenant = action.payload;
      })
      
      // Create tenant
      .addCase(createNewTenant.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNewTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.tenants.unshift(action.payload);
        state.modalState.create = false;
      })
      .addCase(createNewTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update tenant
      .addCase(updateExistingTenant.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateExistingTenant.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tenants.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tenants[index] = action.payload;
        }
        state.modalState.edit = false;
        state.selectedTenant = null;
      })
      .addCase(updateExistingTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete tenant
      .addCase(deleteTenantById.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteTenantById.fulfilled, (state, action) => {
        state.loading = false;
        state.tenants = state.tenants.filter(t => t.id !== action.payload);
        state.modalState.delete = false;
        state.selectedTenant = null;
      })
      .addCase(deleteTenantById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Check code
      .addCase(checkCode.pending, (state) => {
        state.checkingCode = true;
      })
      .addCase(checkCode.fulfilled, (state, action) => {
        state.checkingCode = false;
        state.codeAvailable = action.payload.available;
      })
      .addCase(checkCode.rejected, (state) => {
        state.checkingCode = false;
      });
  },
});

export const {
  setSelectedTenant,
  openModal,
  closeModal,
  setFilters,
  clearFilters,
  setCurrentPage,
  setPageSize,
  setSorting,
  clearError,
} = tenantManagementSlice.actions;

export default tenantManagementSlice.reducer;